import React from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/solid";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { getSFDCData } from "@/entities/Deal/Deal.queries";
import { DetailTableRow } from "../../components/DetailTable";
import { getCompany } from "@/entities/Company";
import {
    NAICS_6_DIGIT_CODE,
    NAICS_INDUSTRY_GROUP_OPTIONS,
} from "@/constants/industries.constants";
import { REVENUE_MODEL_V6 } from "@/entities/CapitalApplication/CapitalApplication.constants";
import { useMemoText } from "../../hooks/useMemoText";
import { MemoTextJsonTypeEnum } from "@/entities/MemoText/MemoText.model";
import { COMING_SOON } from "@/entities/CreditBox/CreditBox.types";
import { DataSource } from "../../components/SourceTag";
import { MetricSource } from "@/features/Metrics/Metrics.types";
import { useFlags } from "@/utils/flagsmith/useFlags";
import { FinanceLabField, getFinanceLabMetrics } from "@/entities/FinanceLab";
import { getLastValue } from "@/utils/metrics/metricsUtils";

interface UseGeneralDataProps {
    opportunityId?: string;
}

export function useGeneralData({ opportunityId }: UseGeneralDataProps = {}) {
    const flags = useFlags();
    const showComingSoon = flags.enabled("show-coming-soon-placeholders");
    const showPreAlphaFields = flags.enabled("show-pre-alpha-fields");
    const useFinanceLabDefault = flags.enabled("use-finance-lab-metrics");
    const sfdcData = useApiQuery(getSFDCData);
    const company = useApiQuery(getCompany);
    const { text } = useMemoText();
    const data = opportunityId
        ? sfdcData.data?.find((data) => data.id === opportunityId)
        : sfdcData.data?.[0];

    const { data: legalNameMetrics } = useApiQuery(getFinanceLabMetrics, {
        params: {
            labels: [FinanceLabField.ORGANIZATION_LEGAL_NAME],
        },
    });

    const { data: crmOpportunityMetrics } = useApiQuery(getFinanceLabMetrics, {
        params: {
            labels: [
                FinanceLabField.DEAL_CRM_OPPORTUNITY_ID,
                FinanceLabField.DEAL_CRM_OPPORTUNITY_NAME,
            ],
        },
    });

    const legalNameValues = legalNameMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_LEGAL_NAME,
    )?.values;

    const legalNameValue = getLastValue(legalNameValues);

    const crmOpportunityIdValues = crmOpportunityMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_CRM_OPPORTUNITY_ID,
    )?.values;

    const crmOpportunityIdValue = getLastValue(crmOpportunityIdValues);

    const crmOpportunityNameValues = crmOpportunityMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_CRM_OPPORTUNITY_NAME,
    )?.values;

    const crmOpportunityNameValue = getLastValue(crmOpportunityNameValues);

    const legalName =
        legalNameValue?.formattedValue || legalNameValue?.value || "-";
    const industry =
        NAICS_INDUSTRY_GROUP_OPTIONS[
            company.data?.capitalApplication.selfReportedIndustry as string
        ];
    const revenueModel =
        REVENUE_MODEL_V6[
            company.data?.capitalApplication.revenueModel as string
        ];
    const linkedin = sfdcData.data?.[0]?.companyPageLinkedinUrl;

    const generalData: DetailTableRow[] = [
        ...(showPreAlphaFields
            ? [
                  {
                      label: "Legal Name",
                      value: legalName,
                      source: MetricSource.FINANCE_LAB,
                      fieldName: FinanceLabField.ORGANIZATION_LEGAL_NAME,
                  },
              ]
            : []),
        {
            label: "Self-Reported Industry",
            value: industry,
            source: DataSource.LEGACY_BACKEND,
            fieldName: "company.capitalApplication.selfReportedIndustry",
        },
        {
            label: "LinkedIn",
            value:
                linkedin && linkedin !== "-"
                    ? React.createElement(
                          "a",
                          {
                              href: linkedin,
                              target: "_blank",
                              rel: "noreferrer",
                              className:
                                  "text-link flex items-center gap-2 group",
                          },
                          `${company.data?.name} - LinkedIn`,
                          React.createElement(ArrowTopRightOnSquareIcon, {
                              className:
                                  "h-4 w-4 fill-none stroke-brand-300 group-hover:stroke-brand-400",
                          }),
                      )
                    : "-",
            source: linkedin && linkedin !== "-" ? DataSource.SFDC : null,
            fieldName: "sfdc.companyPageLinkedinUrl",
        },
        {
            label: "Revenue Model",
            value: revenueModel,
            source: DataSource.LEGACY_BACKEND,
            fieldName: "company.capitalApplication.revenueModel",
        },
    ];

    const sfdcAccount = data?.accountName;

    const crmOpportunityId = useFinanceLabDefault
        ? crmOpportunityIdValue?.formattedValue || crmOpportunityIdValue?.value
        : data?.id;
    const sfdcOpportunityIdSource = useFinanceLabDefault
        ? MetricSource.FINANCE_LAB
        : DataSource.SFDC;
    const sfdcOpportunityIdFieldName = useFinanceLabDefault
        ? FinanceLabField.DEAL_CRM_OPPORTUNITY_ID
        : "sfdc.id";

    const crmOpportunityName = useFinanceLabDefault
        ? crmOpportunityNameValue?.formattedValue ||
          crmOpportunityNameValue?.value
        : data?.opportunityName;
    const sfdcOpportunityNameSource = useFinanceLabDefault
        ? MetricSource.FINANCE_LAB
        : DataSource.SFDC;
    const sfdcOpportunityNameFieldName = useFinanceLabDefault
        ? FinanceLabField.DEAL_CRM_OPPORTUNITY_NAME
        : "sfdc.opportunityName";

    const icmId = company.data?.id;
    const aiNaics =
        NAICS_6_DIGIT_CODE[
            text[MemoTextJsonTypeEnum.COMPANY_METADATA]?.data
                ?.naicsCode as string
        ] ||
        text[MemoTextJsonTypeEnum.COMPANY_METADATA]?.data?.naicsCode ||
        "-";
    const humNaicsCodeList = data?.naicsCodeList;
    const humNaicsIndustry = data?.naicsIndustry;

    const identifiersData: DetailTableRow[] = [
        {
            label: "Hum ID",
            value: icmId,
            source: DataSource.LEGACY_BACKEND,
            fieldName: "company.id",
        },
        ...(showComingSoon ? [{ label: "DUNS", value: COMING_SOON }] : []),
        {
            label: "Account ID",
            value:
                sfdcAccount && sfdcAccount !== "-"
                    ? React.createElement(
                          "a",
                          {
                              href: `https://captec.lightning.force.com/lightning/r/Account/${sfdcAccount}/view`,
                              target: "_blank",
                              rel: "noreferrer",
                              className:
                                  "text-link flex items-center gap-2 group",
                          },
                          sfdcAccount,
                          React.createElement(ArrowTopRightOnSquareIcon, {
                              className:
                                  "h-4 w-4 fill-none stroke-brand-300 group-hover:stroke-brand-400",
                          }),
                      )
                    : "-",
            source: sfdcAccount && sfdcAccount !== "-" ? DataSource.SFDC : null,
            fieldName: "sfdc.accountName",
        },
        {
            label: "Hum NAICS Code",
            value: humNaicsCodeList || "-",
            source: DataSource.SFDC,
            fieldName: "sfdc.naicsCodeList",
        },
        {
            label: "Opportunity Name",
            value: crmOpportunityName || "-",
            source: sfdcOpportunityNameSource,
            fieldName: sfdcOpportunityNameFieldName,
        },
        {
            label: "Hum NAICS Industry",
            value: humNaicsIndustry || "-",
            source: DataSource.SFDC,
            fieldName: "sfdc.naicsIndustry",
        },
        {
            label: "Opportunity ID",
            value:
                crmOpportunityId && crmOpportunityId !== "-"
                    ? React.createElement(
                          "a",
                          {
                              href: `https://captec.lightning.force.com/lightning/r/Opportunity/${crmOpportunityId}/view`,
                              target: "_blank",
                              rel: "noreferrer",
                              className:
                                  "text-link flex items-center gap-2 group",
                          },
                          crmOpportunityId,
                          React.createElement(ArrowTopRightOnSquareIcon, {
                              className:
                                  "h-4 w-4 fill-none stroke-brand-300 group-hover:stroke-brand-400",
                          }),
                      )
                    : "-",
            source:
                crmOpportunityId && crmOpportunityId !== "-"
                    ? sfdcOpportunityIdSource
                    : null,
            fieldName: sfdcOpportunityIdFieldName || "-",
        },
        {
            label: React.createElement(
                "span",
                { className: "flex items-center gap-2" },
                "NAICS",
                React.createElement(
                    "span",
                    {
                        className:
                            "flex items-center justify-center rounded-full bg-slate-600 px-2 py-1",
                    },
                    React.createElement(SparklesIcon, {
                        className: "h-4 w-4",
                    }),
                ),
            ),
            value: aiNaics,
            source: DataSource.MEMO_TEXT,
            fieldName: "memoText.companyMetadata.naicsCode",
        },
    ];

    return {
        generalData,
        identifiersData,
        isLoading: sfdcData.isLoading,
        error: sfdcData.error,
    };
}
